import React, { useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from '../../../utils/reactRouterCompatibility';
import HasCapability from '../../../Route/HasCapability';
import { CircularProgress, Grid, List, ListItem, ListItemIcon, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import ListItemNavLink from '../ListItemNavLink';
import {
	getStoreCouponsRoute,
	getStoreShipmentsRoute,
	getStoreShippingFeesRoute,
	getStoreShopRoute,
	getStoreWarehouseRoute,
	getStoreWizardRoute
} from '../../../../router/routes/store/factory/store';
import StoreIcon from '@mui/icons-material/Store';
import HasRight from '../../../Route/HasRight';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import Badge from '@mui/material/Badge';
import { FaWarehouse } from 'react-icons/fa';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import Button from '../../../common/inputs/Button';
import { refreshShippingsPendingCount } from '../../../../store/actions/store/ShippingAction';
import { deliverableStats } from '../../../../store/actions/store/DeliverableAction';
import { hideMenu } from '../../../../store/actions/CommonAction';
import graphic from '../../../../resources/images/store-wizard/intro.svg';

const MenuStore = () => {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const history = useHistory();

	const ready = useSelector(state => state.common.ready);
	const catalog = useSelector(state => state.store.catalog);
	const loading = useSelector(state => state.store.loading);
	const shippingCount = useSelector(state => state.shipping.shippingsPendingCount);
	const outOfStock = useSelector(state => state.deliverable.outOfStock);
	const realm = useSelector(state => state.realm.selected);
	const isOwner = useSelector(state => state.access.owner);
	const isSupport = useSelector(state => state.access.support);
	const creatingArtist = useSelector(state => state.music.creating);

	const disabled = loading || catalog === null || !ready;
	const displayCreate = ready && !catalog && realm;

	const handleHideMenu = () => dispatch(hideMenu());

	const refreshStats = useCallback(() => {
		if (!catalog || creatingArtist || !isSupport) return;
		dispatch(deliverableStats(catalog.Catalog__, true));
		dispatch(refreshShippingsPendingCount(true));
	}, [dispatch, catalog, creatingArtist, isSupport]);

	useEffect(() => {
		if (!ready) return;
		refreshStats();
	}, [ready, refreshStats]);

	useEffect(() => {
		if (!isSupport) return;
		const refreshInterval = setInterval(refreshStats, 30000);
		return () => clearInterval(refreshInterval);
	}, [isSupport, refreshStats]);

	if (displayCreate && !isOwner) {
		return (
			<Typography sx={{ padding: 2, textAlign: 'center' }}>
				{t('store_ask_owner')}
			</Typography>
		);
	}

	return (
		<HasCapability store hideLoading>
			<List>
				{loading && (
					<ListItem>
						<ListItemIcon>
							<CircularProgress size={15} />
						</ListItemIcon>
					</ListItem>
				)}

				{!displayCreate && (
					<>
						<ListItemNavLink
							tooltip={t('menu_store_shop')}
							disabled={disabled}
							path={getStoreShopRoute()}
							exact={true}
							icon={<StoreIcon />}
							primary={t('menu_store_shop')}
						/>

						<HasRight admin>
							<ListItemNavLink
								tooltip={t('menu_store_coupon')}
								disabled={disabled}
								path={getStoreCouponsRoute()}
								icon={<LocalOfferIcon />}
								primary={t('menu_store_coupon')}
							/>
						</HasRight>

						<ListItemNavLink
							tooltip={t('menu_store_warehouse')}
							disabled={disabled}
							path={getStoreWarehouseRoute()}
							icon={
								<Badge color="secondary" badgeContent={parseInt(outOfStock) || 0}>
									<FaWarehouse size={20} />
								</Badge>
							}
							primary={t('menu_store_warehouse')}
						/>

						<ListItemNavLink
							tooltip={t('menu_store_shipping')}
							disabled={disabled}
							path={getStoreShipmentsRoute()}
							icon={
								<Badge color="primary" badgeContent={parseInt(shippingCount) || 0}>
									<LocalShippingIcon />
								</Badge>
							}
							primary={t('menu_store_shipping')}
						/>

						<HasRight admin>
							<ListItemNavLink
								tooltip={t('menu_store_shipping_fees')}
								disabled={disabled}
								path={getStoreShippingFeesRoute()}
								icon={<MonetizationOnIcon />}
								primary={t('menu_store_shipping_fees')}
							/>
						</HasRight>
					</>
				)}

				{displayCreate && (
					<ListItem
						onClick={() => {
							handleHideMenu();
							history.push(getStoreWizardRoute());
						}}
						sx={{ cursor: 'pointer' }}
					>
						<Grid container spacing={2} justifyContent="center">
							<Grid item sx={{ textAlign: 'center' }}>
								<img src={graphic} alt="store" width="100px" />
							</Grid>
							<Grid item xs={12} />
							<Grid item>
								<Button color="primary" variant="outlined">
									{t('menu_create_store')}
								</Button>
							</Grid>
						</Grid>
					</ListItem>
				)}
			</List>
		</HasCapability>
	);
};

export default MenuStore;

